import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatarCentavos } from '@/pages/loja/utils';
import type { MaquininhaTipo, SituacaoCaixa } from '../types';

const INTERVALO_MS = 3000;
const LIMITE_MS = 15 * 60 * 1000;
const AVISO_LENTO_MS = 10 * 1000;

interface Props {
  numero: number;
  totalCentavos: number;
  pedidoId: number;
  /** Tipo escolhido (null quando a venda foi retomada de uma pendente). */
  tipo: MaquininhaTipo | null;
  aoPago: (situacao: SituacaoCaixa) => void;
  aoVoltar: (mensagem: string | null) => void;
}

// Tela cheia enquanto a maquininha cobra. Confirma o pagamento SOMENTE quando o
// servidor diz `situacao.status === 'pago'` (vem do Mercado Pago).
export default function TelaCartao({
  numero,
  totalCentavos,
  pedidoId,
  tipo,
  aoPago,
  aoVoltar,
}: Props) {
  const [tipoAtual, setTipoAtual] = useState<MaquininhaTipo | null>(tipo);
  const [avisoLento, setAvisoLento] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [erroCancelar, setErroCancelar] = useState<string | null>(null);
  const [cancelarNaMaquininha, setCancelarNaMaquininha] = useState(false);

  const encerradoRef = useRef(false);
  const inicioRef = useRef(Date.now());
  const aoPagoRef = useRef(aoPago);
  const aoVoltarRef = useRef(aoVoltar);
  aoPagoRef.current = aoPago;
  aoVoltarRef.current = aoVoltar;

  useEffect(() => {
    let ativo = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function consultar() {
      if (encerradoRef.current) return;
      try {
        const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
          body: { acao: 'consultar', pedido_id: pedidoId },
        });
        if (!ativo || encerradoRef.current) return;

        if (!error && data?.situacao) {
          const s = data.situacao as SituacaoCaixa;
          if (s.meio === 'pix') setTipoAtual('pix');
          else if (s.meio === 'cartao' && !tipo) setTipoAtual(null);

          if (s.status === 'pago') {
            encerradoRef.current = true;
            aoPagoRef.current(s);
            return;
          }
          const cob = s.cobranca?.status;
          if (cob === 'recusada') {
            encerradoRef.current = true;
            aoVoltarRef.current('Cartão recusado. Tente outro cartão ou outra forma.');
            return;
          }
          if (cob === 'expirada') {
            encerradoRef.current = true;
            aoVoltarRef.current('O tempo acabou. Tente de novo.');
            return;
          }
          if (cob === 'cancelada' || s.status === 'cancelado') {
            encerradoRef.current = true;
            aoVoltarRef.current('Cobrança cancelada.');
            return;
          }

          // A maquininha ainda não pegou a cobrança: avisa, sem parar de consultar.
          if (data.status_mp === 'created' && Date.now() - inicioRef.current >= AVISO_LENTO_MS) {
            setAvisoLento(true);
          }
        }
      } catch {
        /* rede: tenta de novo no próximo ciclo */
      }

      if (!ativo || encerradoRef.current) return;
      if (Date.now() - inicioRef.current >= LIMITE_MS) {
        encerradoRef.current = true;
        aoVoltarRef.current('O tempo acabou. Tente de novo.');
        return;
      }
      timer = setTimeout(consultar, INTERVALO_MS);
    }

    timer = setTimeout(consultar, INTERVALO_MS);
    return () => {
      ativo = false;
      if (timer) clearTimeout(timer);
    };
  }, [pedidoId, tipo]);

  async function cancelar() {
    if (encerradoRef.current) return;
    setCancelando(true);
    setErroCancelar(null);
    try {
      const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'cancelar', pedido_id: pedidoId },
      });
      if (error) {
        setErroCancelar('Sem conexão agora. Tente de novo.');
        setCancelando(false);
        return;
      }
      if (data?.ok) {
        encerradoRef.current = true;
        aoVoltarRef.current(null);
        return;
      }
      if (data?.na_maquininha) {
        // A cobrança já foi para a maquininha: quem cancela é ela. Continua consultando.
        setCancelarNaMaquininha(true);
        setErroCancelar(null);
        setCancelando(false);
        return;
      }
      if (data?.situacao?.status === 'pago') {
        encerradoRef.current = true;
        aoPagoRef.current(data.situacao as SituacaoCaixa);
        return;
      }
      setErroCancelar(data?.erro ?? 'Não foi possível cancelar. Tente de novo.');
      setCancelando(false);
    } catch {
      setErroCancelar('Sem conexão agora. Tente de novo.');
      setCancelando(false);
    }
  }

  const ehPix = tipoAtual === 'pix';
  const instrucao = ehPix ? 'Na maquininha, toque em Pix' : 'Passe ou aproxime o cartão na maquininha';

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-primary-50 px-6 text-center"
      role="dialog"
      aria-modal="true"
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-secondary-500">
        Venda #{numero}
      </p>
      <p className="mt-2 text-5xl font-extrabold tabular-nums text-foreground-950">
        {formatarCentavos(totalCentavos)}
      </p>

      <span className="relative mt-8 flex h-28 w-28 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-primary-200/60" />
        <span className="relative flex h-24 w-24 items-center justify-center rounded-full bg-primary-500 text-white">
          <i className={`${ehPix ? 'ri-qr-code-line' : 'ri-bank-card-line'} text-5xl`} />
        </span>
      </span>

      <h1 className="mt-6 text-xl font-bold text-foreground-950">{instrucao}</h1>
      {ehPix ? (
        <p className="mt-2 max-w-[360px] text-base text-secondary-600">
          Depois, o cliente escaneia o QR que aparece nela.
        </p>
      ) : (
        <p className="mt-2 max-w-[360px] text-base text-secondary-600">
          Aguardando o pagamento… Se o valor não aparecer na maquininha, toque em Atualizar nela.
        </p>
      )}

      {avisoLento && (
        <p className="mt-4 flex max-w-[380px] items-start gap-2 rounded-xl bg-warning-50 px-4 py-3 text-left text-sm font-medium text-warning-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-alert-line" />
          </span>
          A maquininha ainda não pegou. Ela está ligada e na tela Vendas vinculadas? Se precisar,
          toque em Atualizar nela.
        </p>
      )}

      {cancelarNaMaquininha && (
        <p className="mt-4 rounded-xl bg-warning-100 px-4 py-3 text-sm font-semibold text-warning-800">
          Cancele na maquininha (toque em X ou Voltar nela).
        </p>
      )}

      {erroCancelar && (
        <p className="mt-4 rounded-xl bg-danger-50 px-4 py-2 text-sm font-medium text-danger-700">
          {erroCancelar}
        </p>
      )}

      <button
        type="button"
        onClick={cancelar}
        disabled={cancelando}
        className="mt-8 inline-flex h-[52px] w-full max-w-[320px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white text-base font-semibold text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {cancelando ? (
          <>
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-loader-4-line animate-spin" />
            </span>
            Cancelando…
          </>
        ) : (
          'Cancelar'
        )}
      </button>
    </div>
  );
}